import { useMemo } from "react";
import { useTarefas } from "@/hooks/useTarefas";
import { useAuth } from "@/hooks/useAuth";
import {
  STATUS_ABERTOS,
  TAREFA_STATUS_LABEL,
  type Tarefa,
  type TarefaStatus,
} from "@/types/tarefas";

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function isEnvolvido(t: Tarefa, uid: string) {
  return (
    t.responsavel_id === uid ||
    t.criado_por === uid ||
    (t.participantes ?? []).some((p) => p.user_id === uid)
  );
}

export function useTarefasDashboard() {
  const { user, isVendedorScoped } = useAuth();
  const { data: tarefas = [], isLoading } = useTarefas(
    isVendedorScoped() ? { apenasMinhas: true } : undefined
  );

  const stats = useMemo(() => {
    const uid = user?.id ?? "";
    const agora = new Date();
    const hojeIni = startOfDay(agora);
    const hojeFim = endOfDay(agora);
    const em7 = endOfDay(new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000));

    const minhas = uid ? tarefas.filter((t) => isEnvolvido(t, uid)) : [];
    const abertas = tarefas.filter((t) => STATUS_ABERTOS.includes(t.status));
    const minhasAbertas = minhas.filter((t) => STATUS_ABERTOS.includes(t.status));

    const atrasadas = abertas.filter(
      (t) => t.prazo && new Date(t.prazo) < hojeIni
    );
    const hoje = abertas.filter((t) => {
      if (!t.prazo) return false;
      const p = new Date(t.prazo);
      return p >= hojeIni && p <= hojeFim;
    });
    const proximas = abertas.filter((t) => {
      if (!t.prazo) return false;
      const p = new Date(t.prazo);
      return p > hojeFim && p <= em7;
    });

    const porStatus: { status: TarefaStatus; label: string; total: number }[] = (
      Object.keys(TAREFA_STATUS_LABEL) as TarefaStatus[]
    ).map((status) => ({
      status,
      label: TAREFA_STATUS_LABEL[status],
      total: tarefas.filter((t) => t.status === status).length,
    }));

    const recentes = [...tarefas]
      .sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )
      .slice(0, 8);

    return {
      total: tarefas.length,
      abertas: abertas.length,
      minhasAbertas: minhasAbertas.length,
      atrasadas,
      hoje,
      proximas,
      porStatus,
      recentes,
      urgentes: abertas.filter((t) => t.prioridade === "urgente").length,
    };
  }, [tarefas, user?.id]);

  return { isLoading, ...stats };
}
